import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useBuildOk, useStartBuild } from '@/hooks/useFeed'
import { InsufficientSeedsError, SEED_COSTS, useSeedBalance } from '@/hooks/useSeeds'
import { useEffectiveClientId } from '@/hooks/useAuth'
import type { FeedHeadline } from '@/components/feed/feedTypes'

/** Brian's seed glyph (alerts.html SEED). */
export const SEED = (
  <svg viewBox="0 0 13 15" aria-hidden="true"><path d="M6.5 14V7.2M6.5 7.2C6.5 4.3 4.4 2.2 1.5 2.2c0 2.9 2.1 5 5 5zM6.5 8.6c0-2.6 1.9-4.6 4.6-4.6 0 2.6-1.9 4.6-4.6 4.6z" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" /></svg>
)

const WORDS = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten']

/** "Breaking as of 6:41 today" · "yesterday, 4:12 pm" · "Thursday, September 17" (spec §10.4.2: stated plainly). */
export function asOf(t: string | number): string {
  const d = new Date(t), day0 = new Date(); day0.setHours(0, 0, 0, 0)
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }).toLowerCase()
  if (d >= day0) return `Breaking as of ${time.replace(/ (am|pm)$/, '')} today`
  if (d.getTime() >= day0.getTime() - 864e5) return `Breaking as of yesterday, ${time}`
  return 'Breaking as of ' + d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })
}

/** "This story broke six days ago." once it's over a day old (spec §10.4.3), else ''. */
export function ageNotice(t: string): string {
  const days = Math.floor((Date.now() - Date.parse(t)) / 864e5)
  if (days < 1) return ''
  return `This story broke ${days === 1 ? 'yesterday' : (WORDS[days] ?? String(days)) + ' days ago'}.`
}

/** "Fri, Oct 23 · 6:41 pm" */
export function stamp(t: string): string {
  const d = new Date(t)
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) + ' · ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }).toLowerCase()
}

/** A YYYY-MM-DD date as "Fri, Oct 23". */
export function dayOf(iso: string | null | undefined): string {
  if (!iso) return ''
  return new Date(iso.slice(0, 10) + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
}
/** "Monday, Oct 26" */
export function longDay(t: string | null | undefined): string {
  if (!t) return ''
  const d = t.length === 10 ? new Date(t + 'T12:00:00') : new Date(t)
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })
}

// ---------------- toast ----------------
export function useToast() {
  const [t, setT] = useState<{ id: number; body: ReactNode } | null>(null)
  const timer = useRef<number>(0)
  const show = useCallback((body: ReactNode, ms = 3000) => {
    window.clearTimeout(timer.current)
    setT({ id: Date.now(), body })
    timer.current = window.setTimeout(() => setT(null), ms)
  }, [])
  useEffect(() => () => window.clearTimeout(timer.current), [])
  const node = t ? createPortal(<div className="gg gga-toast" role="status" key={t.id}>{t.body}</div>, document.body) : null
  return { show, node }
}

// ---------------- sheet ----------------
export function Sheet({ open, onClose, label, children }: { open: boolean; onClose: () => void; label: string; children: ReactNode }) {
  useEffect(() => {
    if (!open) return
    const k = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', k)
    return () => window.removeEventListener('keydown', k)
  }, [open, onClose])
  if (!open) return null
  return createPortal(
    <div className="gg">
      <div className="gga-scrim" onClick={onClose} />
      <div className="gga-sheet" role="dialog" aria-modal="true" aria-label={label}>
        <div className="grab" />
        {children}
      </div>
    </div>,
    document.body,
  )
}

// ---------------- Build, the way the feed builds ----------------
/**
 * Build · 12 from a queued alert or find. Its headline row is made first
 * (alert_headline / find_headline), then the feed's own background build runs
 * (useStartBuild: the chip ticks −12 now, the Writing bar shows it, the story
 * lands in the Library, and the server charges once it's written). The first
 * Build asks once, as the feed does.
 */
export function useQueueBuild(makeHeadline: (id: string) => Promise<string>, invalidate: unknown[][]) {
  const start = useStartBuild()
  const buildOk = useBuildOk()
  const qc = useQueryClient()
  const clientId = useEffectiveClientId()
  const { data: bal } = useSeedBalance()
  const toast = useToast()
  const [ask, setAsk] = useState<null | { id: string; done?: () => void }>(null)
  const available = bal?.total_available ?? 0
  const cost = SEED_COSTS.build_story

  const run = useCallback(async (id: string, done?: () => void) => {
    if (bal && available < cost) {
      toast.show(<><b>Not enough seeds left for a story.</b> This week’s come back Monday.</>, 3600)
      return false
    }
    try {
      const hid = await makeHeadline(id)
      const { data: h, error } = await supabase.from('headlines').select('*').eq('id', hid).single()
      if (error || !h) throw error ?? new Error('That build did not start. Nothing was charged.')
      start.mutate(h as unknown as FeedHeadline, {
        onError: (e) => toast.show(e instanceof InsufficientSeedsError
          ? <><b>Not enough seeds left for a story.</b> This week’s come back Monday.</>
          : (e as Error).message || 'That build did not start. Nothing was charged.', 3600),
        onSettled: () => invalidate.forEach((k) => qc.invalidateQueries({ queryKey: [...k, clientId] })),
      })
      done?.()
      return true
    } catch (e) {
      toast.show((e as Error).message || 'That build did not start. Nothing was charged.', 3600)
      return false
    }
  }, [available, bal, cost, makeHeadline, start, toast, qc, invalidate, clientId])

  /** Returns false when it didn't start (no seeds), or when it is waiting on the first-build question. */
  const build = useCallback((id: string, done?: () => void): boolean => {
    if (!buildOk.ok) { setAsk({ id, done }); return false }
    void run(id, done)
    return true
  }, [buildOk.ok, run])

  const confirm = (
    <Sheet open={!!ask} onClose={() => setAsk(null)} label="Build this story?">
      <h3>Build this story?</h3>
      <p className="sub">Emma writes the script and caption in your voice. <b>It uses {cost} of your {available.toLocaleString('en-US')} seeds.</b> Seeds come back every Monday; you’ll only be asked this once.</p>
      <div className="two">
        <button type="button" onClick={() => setAsk(null)}>Not now</button>
        <button type="button" className="pri" onClick={() => { const a = ask!; setAsk(null); void buildOk.confirm(); void run(a.id, a.done) }}>Build · {cost} seeds</button>
      </div>
    </Sheet>
  )
  return { build, confirm, toast, cost }
}

/** A topic photo that falls back to nothing (the card's own ground) if it fails. */
export function bgPhoto(url: string | null): React.CSSProperties {
  return url ? { backgroundImage: `url('${url}')` } : {}
}
